// Poster images on the monitor (contract section 5). The current MediaItem's poster is drawn into a canvas
// with the screen's aspect (cover crop, centred), and that canvas is the texture of a clone of the screen
// node's material (unlit, reflection layer kept). A change of poster fades over FADE_S; `posters` with
// several images crossfade every CYCLE_S.
//
// Animation (2026-10-22): posters are animated GIFs / WebPs (Voyage's video.mainPoster). Each is decoded once
// into its frames (anim-image.ts: ImageDecoder, else gifuct-js / own WebP demuxer), kept at the image's own
// size (a few MB). While the music plays, a timer steps through the frames at the image's own delays and draws
// the current one into the canvas (cover crop, pixel blocks), and the texture is uploaded and invalidate()
// called: only when the frame index changed, the monitor is in view and the page is visible. Paused music
// stops the timer on the current frame. Fetches are CORS (R2 allows *).
//
// UV orientation: the screen's UVs are read from the mesh. If U runs right-to-left or V runs bottom-to-top
// (in glTF terms, where v = 0 is the top row of the image), the texture is mirrored to match, so the poster
// is upright for both conventions.
//
// Frame: every poster draw is clipped to an inset rounded rectangle (Voyage's page frame, see FRAME_INSET),
// black around it. Fades composite canvases that already carry the frame.
//
// Video (2026-10-23): while the local music plays, the monitor crossfades from the poster to the item's video
// (MediaItem.video, Voyage video.main), drawn into the same canvas (cover crop, same frame). Paused: the last
// video frame holds for HOLD_S, then the poster fades back. Item change: the new poster fades in first, the new
// video follows POSTER_DWELL_S after the poster's fade, once it plays. The <video> is muted (sound is the audio engine's), loops, and follows the audio
// clock (`clock`): playbackRate nudges within 1 s, a seek beyond. Frames are drawn on requestVideoFrameCallback
// (rAF fallback), at most 30 per second, only while the monitor is in view and the page is visible. Switch:
// TV_DEFAULT ('poster' for now), ?tv=video / ?tv=poster; the mobile tier plays video only with ?tv=video.
// preload="metadata" until the first play; the element is released (src removed) when the item changes, after
// PAUSE_RELEASE_S paused and after OUT_RELEASE_S out of view (paused after OUT_PAUSE_S). Desktop canvas:
// 576x1024, 360x640 while the monitor is small on screen.

import {
  CanvasTexture,
  Color,
  LinearMipmapLinearFilter,
  Mesh,
  MeshBasicMaterial,
  SRGBColorSpace,
  Vector3,
  type Material,
} from 'three';
import type { ScreenInfo } from '../scene/manifest';
import { decodeAnimated, type AnimFrames } from './anim-image';

/**
 * What the monitor shows while the music plays: 'poster' (the animated poster only) or 'video' (the item's video;
 * desktop tier only, the mobile tier needs ?tv=video). `?tv=video` / `?tv=poster` override it.
 * Default 'poster' until the A/B measurements (web/NOTES.md, "TV: video vs animated poster") justify video.
 */
export const TV_DEFAULT: 'poster' | 'video' = 'poster';

const FADE_S = 1.2;
const CYCLE_S = 12;
/** Paused music: the video frame stays this long before the poster fades back. */
const HOLD_S = 1.5;
/** A new item's poster stays this long after its fade-in before the video fades in. */
const POSTER_DWELL_S = 1.5;
/** Video clock check interval while playing, and the thresholds: under SYNC_OK_S nothing, under SYNC_SEEK_S a
 *  playbackRate nudge, beyond that a seek. */
const SYNC_MS = 500;
const SYNC_OK_S = 0.05;
const SYNC_SEEK_S = 1.0;
/** Paused music: the video element is released after this long (stops buffering; made again on play). */
const PAUSE_RELEASE_S = 60;
/** Monitor out of view (or page hidden) while playing: the video pauses after OUT_PAUSE_S, is released after
 *  OUT_RELEASE_S; back in view it starts again at the clock. */
const OUT_PAUSE_S = 3;
const OUT_RELEASE_S = 60;
/** At most 30 video draws + uploads per second. */
const MIN_DRAW_MS = 1000 / 30 - 4;
/** Desktop canvas height when the monitor is small on screen, and the hysteresis band (device pixels). */
const CANVAS_FAR = 640;
const FAR_BELOW_PX = 560;
const NEAR_ABOVE_PX = 720;

type RVFCVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: () => void) => number;
  cancelVideoFrameCallback?: (h: number) => void;
};
/** Draw poster pixels as blocks (Voyage renders its video and posters with image-rendering: crisp-edges). */
const PIXELATED = true;

const _a = new Vector3();

export class ScreenPosters {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private from: HTMLCanvasElement;
  private to: HTMLCanvasElement;
  private texture: CanvasTexture;
  private originals = new Map<Mesh, Material | Material[]>();
  private mats: MeshBasicMaterial[] = [];
  private urls: string[] = [];
  private cycleIndex = 0;
  private cycleTimer = 0;
  private fadeStart = -1;
  private raf = 0;
  private request = 0;
  private applied = false;
  /** URL now shown (after the fade), for tests. */
  current: string | null = null;
  /** Decoded posters by URL (the last few). */
  private cache = new Map<string, Promise<AnimFrames | null>>();
  private anim: AnimFrames | null = null;
  private frame = 0;
  /** Time into the current frame, ms (kept while paused). */
  private frameMs = 0;
  private playing = false;
  private tickTimer = 0;
  private lastTick = 0;
  /** The frame drawn into the canvas (or `to` during a fade), -1 = none yet. */
  private drawnFrame = -1;
  /** For tests and the HUD: frames drawn into the texture since load. */
  framesDrawn = 0;
  private pendingFirst = false;
  /** Draw Voyage's rounded page frame (the monitor); false: fill the screen to its edge. */
  private framed = true;
  /** The item set while there was no poster screen (a room without a monitor). */
  private waiting: { urls: string[]; video: string | null } | null = null;

  /** Audio position in seconds (the video follows it). Set by the caller once the audio engine exists. */
  clock: () => number = () => 0;
  /** Video on this device (TV_DEFAULT / ?tv=, see above). */
  readonly videoAllowed: boolean;
  private videoUrl: string | null = null;
  private video: RVFCVideo | null = null;
  /** The layer shown (or being faded to). */
  private layer: 'poster' | 'video' = 'poster';
  /** Bumped on every item (video URL) change; `posterSeq` = the item whose poster has finished fading in. */
  private itemSeq = 0;
  private posterSeq = -1;
  /** The video presented a frame since it last started playing. */
  private videoLive = false;
  private everPlayed = false;
  private holdTimer = 0;
  private releaseTimer = 0;
  private failedUrl: string | null = null;
  private lastSeen = 0;
  /** The video was paused because the monitor was out of view. */
  private outPaused = false;
  private lastDraw = 0;
  private aspect: number;
  private fullHeight: number;
  /** Monitor height on screen in device pixels (set by the caller; picks the canvas size on desktop). */
  viewHeightPx: () => number = () => Infinity;
  private dwellTimer = 0;
  private syncTimer = 0;
  private vfc = 0;
  private vraf = 0;
  private lastVideoTime = -1;
  /** For tests and the HUD: video frames drawn into the texture since load, and clock corrections. */
  videoFramesDrawn = 0;
  videoSeeks = 0;
  videoUnloads = 0;
  /** Time spent in drawImage for video frames (ms, summed). */
  videoDrawMs = 0;

  constructor(private meshes: Mesh[], info: ScreenInfo | undefined, private mobile: boolean, private invalidate: () => void,
              private isVisible: () => boolean = () => true) {
    const q = new URLSearchParams(location.search).get('tv');
    this.videoAllowed = q === 'video' || (q !== 'poster' && TV_DEFAULT === 'video' && !mobile);
    this.framed = !info || info.id === 'monitor';
    const aspect = info?.width && info?.height ? info.width / info.height : 9 / 16;
    const h = mobile ? 512 : 1024;
    this.aspect = aspect;
    this.fullHeight = h;
    const w = Math.round(h * aspect);
    const mk = () => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; };
    this.canvas = mk();
    this.from = mk();
    this.to = mk();
    this.ctx = this.canvas.getContext('2d')!;
    for (const c of [this.canvas, this.from, this.to]) {
      const g = c.getContext('2d')!;
      g.fillStyle = '#000';
      g.fillRect(0, 0, w, h);
    }
    const tex = new CanvasTexture(this.canvas);
    tex.colorSpace = SRGBColorSpace;
    tex.flipY = false;
    tex.minFilter = LinearMipmapLinearFilter;
    tex.generateMipmaps = true;
    tex.anisotropy = 4;
    this.texture = tex;
    this.orient();
    // show the (black) poster canvas straight away, so the baked placeholder art on the display
    // never flashes before the first poster has loaded
    if (this.meshes.length) this.apply();
  }

  private orient(): void {
    const tex = this.texture;
    tex.repeat.set(1, 1);
    tex.offset.set(0, 0);
    if (this.meshes[0]) {
      const o = uvOrientation(this.meshes[0]);
      if (o.flipU) { tex.repeat.x = -1; tex.offset.x = 1; }
      if (o.flipV) { tex.repeat.y = -1; tex.offset.y = 1; }
    }
  }

  /** Move to other screen meshes (the scene was replaced by another tier). */
  setMeshes(meshes: Mesh[]): void {
    for (const [mesh, mat] of this.originals) mesh.material = mat;
    this.originals.clear();
    for (const m of this.mats) m.dispose();
    this.mats = [];
    this.meshes = meshes;
    this.orient();
    if (this.applied || meshes.length) {
      this.applied = false;
      this.apply();
    }
    // the current item arrived while the room had no poster screen: show it now
    const w = this.waiting;
    if (w && meshes.length) this.set(w.urls, w.video);
    this.invalidate();
  }

  get textureBytes(): number {
    return this.applied ? Math.round(this.canvas.width * this.canvas.height * 4 * 1.333) : 0;
  }

  /** Show these posters (first one now, the rest in a slow cycle; empty = fade to black) and, while the music
   *  plays, this video. */
  set(urls: string[], video: string | null = null): void {
    // a room without a poster screen (the gallery): remember the item, setMeshes shows it once a room with one loads
    if (!this.meshes.length) { this.waiting = { urls, video }; return; }
    this.waiting = null;
    const sameUrls = urls.join('\n') === this.urls.join('\n');
    if (video !== this.videoUrl) {
      this.setVideo(video);
      if (sameUrls) {
        // same poster, new video: back to the poster (already shown), the new video follows once it plays
        this.posterSeq = this.itemSeq;
        if (this.layer === 'video') this.fadeTo('poster');
      }
    }
    if (sameUrls) return;
    this.urls = urls;
    this.cycleIndex = 0;
    clearInterval(this.cycleTimer);
    // the first poster waits on black until the music starts, so the viewer sees it fade in
    if (!this.playing && this.current === null) { this.pendingFirst = true; void this.decode(urls[0] ?? ''); return; }
    void this.show(urls[0] ?? null);
    if (urls.length > 1) {
      this.cycleTimer = window.setInterval(() => {
        if (this.layer === 'video') return; // the video is shown: no poster cycle
        this.cycleIndex = (this.cycleIndex + 1) % this.urls.length;
        void this.show(this.urls[this.cycleIndex]!);
      }, CYCLE_S * 1000);
    }
  }

  private async show(url: string | null): Promise<void> {
    const req = ++this.request;
    const seq = this.itemSeq;
    const anim = url ? await this.decode(url) : null;
    if (req !== this.request) return;
    this.apply();
    this.anim = anim;
    this.frame = 0;
    this.frameMs = 0;
    this.current = url;
    this.schedule();
    clearTimeout(this.dwellTimer);
    this.fadeTo('poster', () => {
      if (seq !== this.itemSeq || this.posterSeq === seq) return;
      this.dwellTimer = window.setTimeout(() => {
        if (seq !== this.itemSeq) return;
        this.posterSeq = seq;
        this.maybeShowVideo();
      }, POSTER_DWELL_S * 1000);
    });
  }

  /**
   * Fade from what is on screen now to `layer`: freeze the canvas as the start, draw the layer's current image
   * as the end, composite over FADE_S. Video frames (and poster frames) that arrive during the fade are drawn
   * into the end canvas.
   */
  private fadeTo(layer: 'poster' | 'video', done?: () => void): void {
    clearTimeout(this.holdTimer);
    this.layer = layer;
    this.from.getContext('2d')!.drawImage(this.canvas, 0, 0);
    if (layer === 'poster') {
      this.drawnFrame = this.frame;
      drawCover(this.to, this.framed, this.anim?.frames[this.frame] ?? null);
    } else {
      drawCover(this.to, this.framed, this.video);
    }
    this.fadeStart = performance.now();
    cancelAnimationFrame(this.raf);
    const step = (now: number) => {
      const k = Math.min(1, (now - this.fadeStart) / (FADE_S * 1000));
      const e = k * k * (3 - 2 * k);
      const c = this.ctx;
      c.globalAlpha = 1;
      c.drawImage(this.from, 0, 0);
      c.globalAlpha = e;
      c.drawImage(this.to, 0, 0);
      c.globalAlpha = 1;
      this.texture.needsUpdate = true;
      this.invalidate();
      if (k < 1) this.raf = requestAnimationFrame(step);
      else { this.fadeStart = -1; done?.(); }
    };
    this.raf = requestAnimationFrame(step);
  }

  /** Music playing: the animation runs; paused: it holds the current frame. */
  setPlaying(playing: boolean): void {
    if (playing === this.playing) return;
    this.playing = playing;
    if (playing && this.pendingFirst) { this.pendingFirst = false; void this.show(this.urls[0] ?? null); }
    this.schedule();
    clearTimeout(this.holdTimer);
    clearTimeout(this.releaseTimer);
    if (playing) {
      this.startVideo();
    } else {
      this.stopVideo();
      const v = this.video;
      if (v) {
        v.preload = 'none';
        // paused a while: drop the element so the browser stops buffering (made again on play)
        this.releaseTimer = window.setTimeout(() => { if (!this.playing) this.unloadVideo(); }, PAUSE_RELEASE_S * 1000);
      }
      // hold the last video frame a moment, then the poster
      if (this.layer === 'video') {
        this.holdTimer = window.setTimeout(() => { if (!this.playing && this.layer === 'video') this.fadeTo('poster'); }, HOLD_S * 1000);
      }
    }
  }

  // ------------------------------------------------------------------ video

  /** The current item's video (null = none). The old element is released. */
  private setVideo(url: string | null): void {
    this.releaseVideo();
    clearTimeout(this.releaseTimer);
    this.itemSeq++;
    this.videoUrl = url;
    this.createVideo();
    if (this.playing) this.startVideo();
  }

  /** Make the <video> for `videoUrl` (if video is allowed here and the URL has not failed). */
  private createVideo(): void {
    const url = this.videoUrl;
    if (this.video || !url || url === this.failedUrl || !this.videoAllowed || !this.meshes.length) return;
    const v = document.createElement('video') as RVFCVideo;
    v.crossOrigin = 'anonymous';
    v.muted = true;
    v.defaultMuted = true;
    v.loop = true; // shorter than the audio: it loops (the clock is taken modulo its duration)
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.preload = this.everPlayed ? 'auto' : 'metadata';
    v.addEventListener('error', () => {
      if (this.video !== v) return;
      console.warn('[poster] video failed, poster only', url, v.error?.message ?? v.error?.code);
      this.failedUrl = url;
      this.releaseVideo();
      if (this.layer === 'video') this.fadeTo('poster');
    });
    v.addEventListener('loadedmetadata', () => { if (this.video === v && this.playing) this.sync(true); });
    v.src = url;
    this.video = v;
  }

  private releaseVideo(): void {
    const v = this.video;
    if (!v) return;
    this.stopVideo();
    this.video = null;
    this.videoUnloads++;
    v.removeAttribute('src');
    v.load();
  }

  /** Drop the element but keep the item's URL (paused or out of view for a while). If the video was shown, the
   *  poster is put back without a fade (nobody is looking, or the music is paused and the poster is up). */
  private unloadVideo(): void {
    if (!this.video) return;
    this.releaseVideo();
    if (this.layer === 'video' && this.fadeStart < 0) {
      this.layer = 'poster';
      this.drawnFrame = this.frame;
      drawCover(this.canvas, this.framed, this.anim?.frames[this.frame] ?? null);
      this.texture.needsUpdate = true;
      this.invalidate();
    }
  }

  /** Music playing: start the video at the audio position and follow the clock. */
  private startVideo(): void {
    this.createVideo();
    const v = this.video;
    if (!v) return;
    this.everPlayed = true;
    if (v.preload !== 'auto') v.preload = 'auto';
    this.lastSeen = performance.now();
    this.outPaused = false;
    this.sync(true);
    v.play().catch((e) => { if (this.video === v && this.playing) console.warn('[poster] video play failed, poster only', e); });
    this.videoLive = false;
    this.requestFrames();
    clearInterval(this.syncTimer);
    this.syncTimer = window.setInterval(() => this.tickVideo(), SYNC_MS);
  }

  private stopVideo(): void {
    const v = this.video;
    clearInterval(this.syncTimer);
    this.syncTimer = 0;
    v?.cancelVideoFrameCallback?.(this.vfc);
    cancelAnimationFrame(this.vraf);
    this.vfc = this.vraf = 0;
    this.videoLive = false;
    if (v && !v.paused) v.pause();
  }

  /** Every SYNC_MS while the music plays: out of view for a while -> pause (then release) the video; back in view
   *  -> start it again at the clock; else follow the clock and pick the canvas size. */
  private tickVideo(): void {
    const now = performance.now();
    const seen = !document.hidden && this.isVisible();
    if (seen) this.lastSeen = now;
    const away = now - this.lastSeen;
    if (!seen && away > OUT_PAUSE_S * 1000) {
      const v = this.video;
      if (v && !v.paused) { v.pause(); this.outPaused = true; }
      if (away > OUT_RELEASE_S * 1000 && this.video) {
        this.unloadVideo(); // stops this timer too: keep watching for the monitor to come back into view
        this.outPaused = true;
        this.syncTimer = window.setInterval(() => this.tickVideo(), SYNC_MS);
      }
      return;
    }
    if (seen && this.outPaused) {
      this.outPaused = false;
      if (!this.video) { this.startVideo(); return; } // released: made again, the poster shows until it plays
      this.sync(true);
      this.video.play().catch(() => {});
    }
    this.sync(false);
    if (seen && this.layer === 'video') this.fitCanvas();
  }

  /** Follow the audio clock: a nudge of playbackRate when slightly off, a seek when far off (or `hard`). */
  private sync(hard: boolean): void {
    const v = this.video;
    if (!v || v.readyState < 1) return;
    const d = v.duration;
    const pos = this.clock();
    const target = Number.isFinite(d) && d > 0 ? pos % d : pos;
    let diff = v.currentTime - target;
    if (Number.isFinite(d) && d > 0) { if (diff > d / 2) diff -= d; else if (diff < -d / 2) diff += d; }
    const a = Math.abs(diff);
    if ((hard && a > 0.2) || a > SYNC_SEEK_S) {
      v.currentTime = target;
      v.playbackRate = 1;
      this.videoSeeks++;
    } else if (a > SYNC_OK_S) {
      v.playbackRate = 1 - Math.max(-0.2, Math.min(0.2, diff * 0.5));
    } else if (v.playbackRate !== 1) {
      v.playbackRate = 1;
    }
    if (this.playing && !this.outPaused && v.paused && !v.ended) v.play().catch(() => {});
  }

  /**
   * Canvas height for the monitor's size on screen (desktop tier): CANVAS_FAR when it covers fewer than
   * FAR_BELOW_PX device pixels in height, the full size above NEAR_ABOVE_PX (hysteresis between). The mobile
   * tier keeps its fixed 512.
   */
  private fitCanvas(): void {
    if (this.mobile || this.fadeStart >= 0) return;
    const px = this.viewHeightPx();
    const h = this.canvas.height;
    const want = px < FAR_BELOW_PX ? CANVAS_FAR : px > NEAR_ABOVE_PX ? this.fullHeight : h;
    if (want !== h) this.resize(want);
  }

  private resize(h: number): void {
    const w = Math.round(h * this.aspect);
    for (const c of [this.canvas, this.from, this.to]) { c.width = w; c.height = h; }
    // the GPU texture has a fixed size: drop it, three makes a new one on the next upload
    this.texture.dispose();
    if (this.layer === 'video') drawCover(this.canvas, this.framed, this.video);
    else drawCover(this.canvas, this.framed, this.anim?.frames[this.drawnFrame] ?? null);
    this.texture.needsUpdate = true;
    this.invalidate();
  }

  /** One callback per presented video frame (requestVideoFrameCallback), else rAF while playing. */
  private requestFrames(): void {
    const v = this.video;
    if (!v) return;
    v.cancelVideoFrameCallback?.(this.vfc);
    cancelAnimationFrame(this.vraf);
    if (v.requestVideoFrameCallback) {
      const loop = () => {
        if (this.video !== v || !this.playing) return;
        this.onVideoFrame();
        this.vfc = v.requestVideoFrameCallback!(loop);
      };
      this.vfc = v.requestVideoFrameCallback(loop);
    } else {
      const loop = () => {
        if (this.video !== v || !this.playing) return;
        if (v.readyState >= 2 && v.currentTime !== this.lastVideoTime) {
          this.lastVideoTime = v.currentTime;
          this.onVideoFrame();
        }
        this.vraf = requestAnimationFrame(loop);
      };
      this.vraf = requestAnimationFrame(loop);
    }
  }

  private onVideoFrame(): void {
    if (!this.videoLive) { this.videoLive = true; this.maybeShowVideo(); }
    if (this.layer !== 'video' || document.hidden || !this.isVisible()) return;
    const now = performance.now();
    if (now - this.lastDraw < MIN_DRAW_MS) return; // at most 30 draws per second
    this.lastDraw = now;
    if (this.fadeStart >= 0) { drawCover(this.to, this.framed, this.video); return; } // the fade composites it
    const t0 = performance.now();
    drawCover(this.canvas, this.framed, this.video);
    this.videoDrawMs += performance.now() - t0;
    this.texture.needsUpdate = true;
    this.videoFramesDrawn++;
    this.invalidate();
  }

  /** Fade to the video once the music plays, the item's poster has faded in and the video presents frames. */
  private maybeShowVideo(): void {
    if (!this.playing || !this.video || !this.videoLive || this.layer === 'video') return;
    if (this.fadeStart >= 0 || this.posterSeq !== this.itemSeq) return;
    this.apply();
    this.fitCanvas();
    this.fadeTo('video');
  }

  /** For tests and the HUD. */
  get videoState(): { url: string | null; layer: string; time: number; paused: boolean; readyState: number; rate: number; canvas: string; buffered: number } {
    const v = this.video;
    const b = v?.buffered;
    return {
      url: v ? this.videoUrl : null, layer: this.layer, time: v?.currentTime ?? 0, paused: v?.paused ?? true,
      readyState: v?.readyState ?? 0, rate: v?.playbackRate ?? 1, canvas: `${this.canvas.width}x${this.canvas.height}`,
      buffered: b && b.length ? +(b.end(b.length - 1) - (v?.currentTime ?? 0)).toFixed(1) : 0,
    };
  }

  private decode(url: string): Promise<AnimFrames | null> {
    let p = this.cache.get(url);
    if (!p) {
      p = decodeAnimated(url).catch((e) => {
        console.warn('[poster] could not load', url, e);
        this.cache.delete(url);
        return null;
      });
      this.cache.set(url, p);
      // keep the last 4 posters decoded; close the frames of older ones
      while (this.cache.size > 4) {
        const [oldUrl, old] = this.cache.entries().next().value!;
        this.cache.delete(oldUrl);
        void old.then((a) => { if (a && a !== this.anim) for (const f of a.frames) f.close(); });
      }
    } else {
      this.cache.delete(url);
      this.cache.set(url, p); // most recently used last
    }
    return p;
  }

  /** (Re)arm the frame timer: only while playing and the poster has more than one frame. */
  private schedule(): void {
    clearTimeout(this.tickTimer);
    this.tickTimer = 0;
    const a = this.anim;
    if (!this.playing || !a || a.frames.length < 2) return;
    this.lastTick = performance.now();
    const wait = Math.max(4, a.delays[this.frame]! - this.frameMs);
    this.tickTimer = window.setTimeout(this.tick, wait);
  }

  private tick = () => {
    const a = this.anim;
    if (!a || !this.playing) return;
    const now = performance.now();
    this.frameMs += now - this.lastTick;
    // advance by elapsed time (a late timer skips frames instead of slowing the animation down)
    let guard = a.frames.length;
    while (this.frameMs >= a.delays[this.frame]! && guard-- > 0) {
      this.frameMs -= a.delays[this.frame]!;
      this.frame = (this.frame + 1) % a.frames.length;
    }
    if (guard <= 0) this.frameMs %= a.total;
    this.drawCurrent();
    this.schedule();
  };

  /** Draw the current frame if it is not drawn yet, the monitor is in view and the page is visible. */
  private drawCurrent(): void {
    const a = this.anim;
    if (!a || this.layer !== 'poster' || this.frame === this.drawnFrame || document.hidden || !this.isVisible()) return;
    this.drawnFrame = this.frame;
    const bmp = a.frames[this.frame]!;
    if (this.fadeStart >= 0) {
      drawCover(this.to, this.framed, bmp); // the fade loop composites it on its next step
      return;
    }
    drawCover(this.canvas, this.framed, bmp);
    this.texture.needsUpdate = true;
    this.framesDrawn++;
    this.invalidate();
  }

  /** Swap the screen materials for the poster material (once). */
  private apply(): void {
    if (this.applied) return;
    this.applied = true;
    for (const mesh of this.meshes) {
      this.originals.set(mesh, mesh.material);
      const old = mesh.material as MeshBasicMaterial;
      const m = old.clone() as MeshBasicMaterial;
      m.onBeforeCompile = old.onBeforeCompile;
      m.customProgramCacheKey = old.customProgramCacheKey;
      m.userData = old.userData;
      m.map = this.texture;
      m.vertexColors = false;
      m.color = new Color(1, 1, 1);
      m.toneMapped = false; // a display: keep it bright and readable whatever the scene's tone mapping
      m.needsUpdate = true;
      mesh.material = m;
      this.mats.push(m);
    }
  }
}

/**
 * Voyage's page frame on the poster (2026-10-23): Voyage clips its full-viewport video with
 * clip-path: inset(0.5em round calc(var(--border-radius) / 1.5)) on a black / Canvas page, which is 8px and
 * 21.33px at 1440x900. The poster keeps the same proportions of the canvas height (the canvas is portrait;
 * Voyage on a 390x844 phone gives 8px / 24.6px, 0.95% / 2.9% of its height, close to these), with the screen's
 * black around it. 576x1024 canvas: inset 9.1px, radius 24.3px.
 */
const FRAME_INSET = 8 / 900;
const FRAME_RADIUS = 21.333 / 900;

/** Cover crop, centred, inside the rounded frame: scale so the image fills the canvas, crop the overflow equally
 *  on both sides, clip to the inset rounded rectangle; black outside it. */
function drawCover(c: HTMLCanvasElement, framed: boolean, img: ImageBitmap | HTMLVideoElement | null): void {
  const g = c.getContext('2d')!;
  g.fillStyle = '#000';
  g.fillRect(0, 0, c.width, c.height);
  if (!img) return;
  const iw = img instanceof HTMLVideoElement ? img.videoWidth : img.width;
  const ih = img instanceof HTMLVideoElement ? img.videoHeight : img.height;
  if (!iw || !ih) return;
  const scale = Math.max(c.width / iw, c.height / ih);
  const sw = c.width / scale, sh = c.height / scale;
  // the page frame only on the monitor; other poster screens (the gallery's oval) are filled to the edge, so no black
  // shows at their rim (the screen runs under its own frame)
  const inset = framed ? c.height * FRAME_INSET : 0, radius = framed ? c.height * FRAME_RADIUS : 0;
  g.save();
  g.beginPath();
  roundRect(g, inset, inset, c.width - 2 * inset, c.height - 2 * inset, radius);
  g.clip();
  g.imageSmoothingEnabled = !PIXELATED;
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, c.width, c.height);
  g.restore();
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  if (typeof g.roundRect === 'function') { g.roundRect(x, y, w, h, r); return; }
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

/**
 * Which way U and V run on the screen mesh, in world space. Right = the screen's right as seen from the front
 * (up x normal); up = world +Y projected on the screen.
 */
export function uvOrientation(mesh: Mesh): { flipU: boolean; flipV: boolean } {
  const g = mesh.geometry;
  const pos = g.attributes.position, uv = g.attributes.uv, nrm = g.attributes.normal;
  if (!pos || !uv) return { flipU: false, flipV: false };
  mesh.updateMatrixWorld(true);
  const n = new Vector3(0, 0, 1);
  if (nrm) n.fromBufferAttribute(nrm, 0).transformDirection(mesh.matrixWorld);
  const up = new Vector3(0, 1, 0);
  const right = new Vector3().crossVectors(up, n).normalize();
  // covariance of u with the right coordinate and of v with the up coordinate
  let su = 0, sv = 0, sx = 0, sy = 0, suv = 0, svy = 0;
  const count = pos.count;
  for (let i = 0; i < count; i++) {
    _a.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
    const x = _a.dot(right), y = _a.y, u = uv.getX(i), v = uv.getY(i);
    su += u; sv += v; sx += x; sy += y; suv += u * x; svy += v * y;
  }
  const covU = suv / count - (su / count) * (sx / count);
  const covV = svy / count - (sv / count) * (sy / count);
  // glTF: v grows downward on an upright image, so v growing with world up means the image must be flipped
  return { flipU: covU < 0, flipV: covV > 0 };
}
